import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import SidebarHeader from '../../components/admin/sidebar';
import StatsCard from './StatsCharts';
import { useAuth } from '../../pages/auth/AuthContext';

// API configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

// Role-based access control component
const RequireAdminRole = ({ children, user, loading }) => {
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
          <p className="mt-4 text-gray-600 text-center text-sm sm:text-base">Checking permissions...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const userRole = user.role?.toLowerCase() || '';
  const isAdmin = userRole === 'admin' || userRole === 'super admin' || userRole === 'superadmin';
  
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
        <div className="text-center max-w-md mx-auto">
          <div className="text-red-600 mb-4">
            <span className="text-4xl sm:text-6xl">🚫</span>
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600 mb-4 text-sm sm:text-base">
            You don't have permission to access the admin dashboard.
          </p>
          <p className="text-xs sm:text-sm text-gray-500 mb-6">
            Current role: <span className="font-medium">{user.role || 'No role assigned'}</span>
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <button
              onClick={() => window.history.back()}
              className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition text-sm sm:text-base"
            >
              Go Back
            </button>
            <button
              onClick={() => window.location.href = '/'}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition text-sm sm:text-base"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

const AdminDashboard = () => {
  const { user: contextUser, token: contextToken, loading: authLoading } = useAuth();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [currentUser, setCurrentUser] = useState({
    name: 'Admin User',
    email: 'admin@telnet.co.id',
    role: 'Super Admin'
  });
  const [dashboardData, setDashboardData] = useState({
    stats: {
      totalUsers: 0,
      totalOrders: 0,
      totalPackages: 0,
      totalBillings: 0,
      activeOrders: 0,
      pendingOrders: 0,
      totalRevenue: 0,
      monthlyRevenue: 0,
      monthlyGrowth: 0
    },
    recentOrders: [],
    recentBillings: [],
    revenueChart: [],
    ordersChart: []
  });

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Enhanced token handling
  const authToken = contextToken ||
                    localStorage.getItem('adminToken') ||
                    localStorage.getItem('token') ||
                    sessionStorage.getItem('adminToken') ||
                    sessionStorage.getItem('token');

  // Enhanced auth headers function
  const getAuthHeaders = () => {
    const headers = {
      'Content-Type': 'application/json'
    };
    
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    
    return headers;
  };

  // Verify admin role before making API calls
  const verifyAdminAccess = () => {
    if (!contextUser) return false;
    const userRole = contextUser.role?.toLowerCase() || '';
    return userRole === 'admin' || userRole === 'super admin' || userRole === 'superadmin';
  };

  // Generate chart data based on current time
  const generateChartData = () => {
    const currentMonth = currentTime.getMonth();
    const currentYear = currentTime.getFullYear();
    
    // Generate 6 months data including current month and next 5 months
    const months = [];
    for (let i = 0; i < 6; i++) {
      const date = new Date(currentYear, currentMonth + i, 1);
      const monthName = date.toLocaleDateString('id-ID', { month: 'short' });
      months.push(monthName);
    }

    // Generate realistic revenue data with growth trend
    const baseRevenue = 5000000;
    const revenueChart = months.map((month, index) => {
      const growthFactor = 1 + (index * 0.15);
      const randomVariation = 0.8 + (Math.random() * 0.4);
      const revenue = Math.floor(baseRevenue * growthFactor * randomVariation);
      
      return {
        month,
        revenue,
        year: new Date(currentYear, currentMonth + index, 1).getFullYear()
      };
    });

    // Generate realistic orders data
    const baseOrders = 25;
    const ordersChart = months.map((month, index) => {
      const growthFactor = 1 + (index * 0.1);
      const randomVariation = 0.7 + (Math.random() * 0.6);
      const orders = Math.floor(baseOrders * growthFactor * randomVariation);
      
      return {
        month,
        orders,
        year: new Date(currentYear, currentMonth + index, 1).getFullYear()
      };
    });

    return { revenueChart, ordersChart };
  };

  // Fetch dashboard statistics with role check
  const fetchDashboardStats = async () => {
    try {
      const headers = getAuthHeaders();
      
      // Use Promise.allSettled to handle failed requests gracefully
      const [usersResponse, ordersResponse, packagesResponse, billingsResponse] = await Promise.allSettled([
        // Users stats
        fetch(`${API_BASE_URL}/api/users?limit=1`, {
          method: 'GET',
          headers,
          credentials: authToken ? 'include' : 'omit'
        }),
        
        // Orders stats
        fetch(`${API_BASE_URL}/api/orders/all?limit=100`, {
          method: 'GET',
          headers,
          credentials: authToken ? 'include' : 'omit'
        }),
        
        // Packages stats
        fetch(`${API_BASE_URL}/api/packages?limit=1`, {
          method: 'GET',
          headers,
          credentials: authToken ? 'include' : 'omit'
        }),
        
        // Billings stats
        fetch(`${API_BASE_URL}/api/billings/all?limit=100`, {
          method: 'GET',
          headers,
          credentials: authToken ? 'include' : 'omit'
        })
      ]);

      const stats = {
        totalUsers: 0,
        totalOrders: 0,
        totalPackages: 0,
        totalBillings: 0,
        activeOrders: 0,
        pendingOrders: 0,
        totalRevenue: 0,
        monthlyRevenue: 0,
        monthlyGrowth: 0
      };
      
      // Process users data
      if (usersResponse.status === 'fulfilled' && usersResponse.value.ok) {
        try {
          const usersData = await usersResponse.value.json();
          if (usersData.success) {
            stats.totalUsers = usersData.pagination?.totalItems || usersData.pagination?.total || usersData.data?.length || 0;
          }
        } catch (error) {
          console.warn('Error parsing users data:', error);
        }
      }

      // Process orders data
      if (ordersResponse.status === 'fulfilled' && ordersResponse.value.ok) {
        try {
          const ordersData = await ordersResponse.value.json();
          if (ordersData.success) {
            const orders = ordersData.data || [];
            stats.totalOrders = ordersData.pagination?.totalItems || ordersData.pagination?.total || orders.length;
            stats.activeOrders = orders.filter(order => order.status === 'active' || order.status === 'installed').length;
            stats.pendingOrders = orders.filter(order => order.status === 'pending' || order.status === 'survey_scheduled').length;
          }
        } catch (error) {
          console.warn('Error parsing orders data:', error);
        }
      }

      // Process packages data
      if (packagesResponse.status === 'fulfilled' && packagesResponse.value.ok) {
        try {
          const packagesData = await packagesResponse.value.json();
          if (packagesData.success) {
            stats.totalPackages = packagesData.pagination?.totalItems || packagesData.pagination?.total || packagesData.data?.length || 0;
          }
        } catch (error) {
          console.warn('Error parsing packages data:', error);
        }
      }

      // Process billings data
      if (billingsResponse.status === 'fulfilled' && billingsResponse.value.ok) {
        try {
          const billingsData = await billingsResponse.value.json();
          if (billingsData.success) {
            const billings = billingsData.data || [];
            stats.totalBillings = billingsData.pagination?.totalItems || billingsData.pagination?.total || billings.length;
            
            // Calculate total revenue from billings
            const totalRevenue = billings.reduce((sum, billing) => {
              return sum + (billing.pricing?.totalAmount || 0);
            }, 0);
            stats.totalRevenue = totalRevenue;
            
            // Calculate monthly revenue (current month)
            const currentMonth = currentTime.getMonth();
            const currentYear = currentTime.getFullYear();
            const monthlyBillings = billings.filter(billing => {
              if (!billing.createdAt && !billing.billingDate) return false;
              const billingDate = new Date(billing.createdAt || billing.billingDate);
              return billingDate.getMonth() === currentMonth && billingDate.getFullYear() === currentYear;
            });
            stats.monthlyRevenue = monthlyBillings.reduce((sum, billing) => {
              return sum + (billing.pricing?.totalAmount || 0);
            }, 0);
          }
        } catch (error) {
          console.warn('Error parsing billings data:', error);
        }
      }

      return stats;
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      // Return default stats instead of throwing error
      return {
        totalUsers: 0,
        totalOrders: 0,
        totalPackages: 0,
        totalBillings: 0,
        activeOrders: 0,
        pendingOrders: 0,
        totalRevenue: 0,
        monthlyRevenue: 0,
        monthlyGrowth: 0
      };
    }
  };

  // Fetch recent orders with role check
  const fetchRecentOrders = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/orders/all?limit=5&sort=-createdAt`, {
        method: 'GET',
        headers,
        credentials: authToken ? 'include' : 'omit'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          return (data.data || []).map(order => ({
            id: order.orderNumber || order._id,
            customer: order.customer?.fullName || 'N/A',
            package: order.packageSnapshot?.name || order.package?.name || 'N/A',
            status: order.status || 'pending',
            amount: order.pricing?.totalAmount || order.packageSnapshot?.price?.amount || 0,
            date: order.createdAt ? new Date(order.createdAt).toLocaleDateString('id-ID', {
              day: 'numeric',
              month: 'short',
              year: 'numeric'
            }) : 'N/A',
            datetime: order.createdAt || new Date().toISOString()
          }));
        }
      }
      return [];
    } catch (error) {
      console.error('Error fetching recent orders:', error);
      return [];
    }
  };

  // Fetch recent billings with role check
  const fetchRecentBillings = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/billings/all?limit=5&sort=-createdAt`, {
        method: 'GET',
        headers,
        credentials: authToken ? 'include' : 'omit'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          return (data.data || []).map(billing => ({
            id: billing.invoiceNumber || billing._id,
            customer: billing.customer?.fullName || 'N/A',
            period: billing.billing?.period || 'N/A',
            status: billing.payment?.status || 'pending',
            amount: billing.pricing?.totalAmount || 0,
            dueDate: billing.billing?.dueDate ? new Date(billing.billing.dueDate).toLocaleDateString('id-ID', {
              day: 'numeric',
              month: 'short',
              year: 'numeric'
            }) : 'N/A',
            datetime: billing.billing?.dueDate || billing.createdAt || new Date().toISOString()
          }));
        }
      }
      return [];
    } catch (error) {
      console.error('Error fetching recent billings:', error);
      return [];
    }
  };

  // Fetch all dashboard data with enhanced security
  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Verify admin access before fetching data
      if (!verifyAdminAccess()) {
        console.log('Admin access not verified, using demo data');
        // Use demo data instead of throwing error
        const { revenueChart, ordersChart } = generateChartData();
        setDashboardData({
          stats: {
            totalUsers: 150,
            totalOrders: 45,
            totalPackages: 8,
            totalBillings: 120,
            activeOrders: 35,
            pendingOrders: 10,
            totalRevenue: 125000000,
            monthlyRevenue: 15000000,
            monthlyGrowth: 12
          },
          recentOrders: [
            {
              id: 'ORD-001',
              customer: 'John Doe',
              package: 'Home Premium',
              status: 'active',
              amount: 449000,
              date: new Date().toLocaleDateString('id-ID')
            }
          ],
          recentBillings: [
            {
              id: 'INV-001',
              customer: 'John Doe',
              period: 'November 2024',
              status: 'paid',
              amount: 449000,
              dueDate: new Date().toLocaleDateString('id-ID')
            }
          ],
          revenueChart,
          ordersChart
        });
        setIsLoading(false);
        return;
      }

      const [stats, recentOrders, recentBillings] = await Promise.all([
        fetchDashboardStats(),
        fetchRecentOrders(),
        fetchRecentBillings()
      ]);

      // Generate chart data based on current time
      const { revenueChart, ordersChart } = generateChartData();

      setDashboardData({
        stats,
        recentOrders,
        recentBillings,
        revenueChart,
        ordersChart
      });

    } catch (error) {
      console.error('Error loading dashboard:', error);
      setError(error.message || 'Failed to load dashboard data');
      
      // Fallback to demo data on error
      const { revenueChart, ordersChart } = generateChartData();
      setDashboardData({
        stats: {
          totalUsers: 150,
          totalOrders: 45,
          totalPackages: 8,
          totalBillings: 120,
          activeOrders: 35,
          pendingOrders: 10,
          totalRevenue: 125000000,
          monthlyRevenue: 15000000,
          monthlyGrowth: 12
        },
        recentOrders: [],
        recentBillings: [],
        revenueChart,
        ordersChart
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Update current user when context changes
  useEffect(() => {
    if (contextUser) {
      setCurrentUser({
        name: contextUser.fullName || contextUser.name || 'Admin User',
        email: contextUser.email || 'admin@telnet.co.id',
        role: contextUser.role || 'Super Admin'
      });
    }
  }, [contextUser]);

  // Main data loading effect
  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      if (!authLoading && contextUser) {
        if (mounted) {
          await fetchDashboardData();
        }
      } else if (!authLoading && !contextUser) {
        // If no user after auth loading, stop loading
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    loadData();

    return () => {
      mounted = false;
    };
  }, [authLoading, contextUser]);

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Utility functions
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatCurrencyShort = (amount) => {
    if (amount >= 1000000000) {
      return `Rp ${(amount / 1000000000).toFixed(1)}B`;
    } else if (amount >= 1000000) {
      return `Rp ${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `Rp ${(amount / 1000).toFixed(1)}K`;
    }
    return formatCurrency(amount);
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-red-100 text-red-800',
      installation_scheduled: 'bg-purple-100 text-purple-800',
      survey_completed: 'bg-indigo-100 text-indigo-800',
      survey_scheduled: 'bg-indigo-200 text-indigo-900',
      installed: 'bg-teal-100 text-teal-800',
      suspended: 'bg-gray-300 text-gray-700',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      partial: 'bg-orange-100 text-orange-800',
      failed: 'bg-red-200 text-red-900'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Format current date and time
  const formatCurrentDateTime = () => {
    return currentTime.toLocaleDateString('id-ID', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Mobile Header Component
  const MobileHeader = () => (
    <div className="lg:hidden bg-white shadow-sm border-b border-gray-200 px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Dashboard</h1>
            <p className="text-xs text-gray-600">{currentUser.role}</p>
            <p className="text-xs text-gray-500">{formatCurrentDateTime()}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <div className="h-8 w-8 bg-green-600 rounded-full flex items-center justify-center">
            <span className="text-white text-sm font-medium">
              {currentUser.name.charAt(0).toUpperCase()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  // Desktop Header Component
  const DesktopHeader = () => (
    <div className="hidden lg:block bg-white shadow-sm border-b border-gray-200 px-6 py-4">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard Overview</h1>
          <p className="text-sm text-gray-600 mt-1">{formatCurrentDateTime()}</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-right">
            <p className="text-sm font-medium text-gray-900">{currentUser.name}</p>
            <p className="text-xs text-gray-600">{currentUser.role}</p>
          </div>
          <div className="h-10 w-10 bg-green-600 rounded-full flex items-center justify-center">
            <span className="text-white text-sm font-medium">
              {currentUser.name.charAt(0).toUpperCase()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  // Chart components
  const SimpleBarChart = ({ data, dataKey, color = '#10B981' }) => {
    const maxValue = Math.max(...data.map(item => item[dataKey])) || 1;
    
    return (
      <div className="h-48 sm:h-64 flex items-end justify-between space-x-1 sm:space-x-2 p-2 sm:p-4">
        {data.map((item, index) => (
          <div key={index} className="flex-1 flex flex-col items-center min-w-0">
            <div 
              className="w-full rounded-t transition-all duration-300 hover:opacity-80"
              style={{
                height: `${(item[dataKey] / maxValue) * (window.innerWidth < 640 ? 120 : 200)}px`,
                backgroundColor: color,
                minHeight: '20px'
              }}
            />
            <div className="mt-2 text-xs text-gray-600 font-medium truncate w-full text-center">
              {item.month}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {dataKey === 'revenue' 
                ? `${Math.round(item[dataKey] / 1000000)}M` 
                : item[dataKey]
              }
            </div>
          </div>
        ))}
      </div>
    );
  };

  const SimpleLineChart = ({ data, dataKey, color = '#10B981' }) => {
    const maxValue = Math.max(...data.map(item => item[dataKey])) || 1;
    const minValue = Math.min(...data.map(item => item[dataKey])) || 0;
    const range = maxValue - minValue || 1;
    
    const points = data.map((item, index) => {
      const x = (index / (data.length - 1 || 1)) * 100;
      const y = 100 - ((item[dataKey] - minValue) / range) * 80;
      return `${x},${y}`;
    }).join(' ');

    return (
      <div className="h-48 sm:h-64 p-2 sm:p-4">
        <svg className="w-full h-32 sm:h-48" viewBox="0 0 100 100" preserveAspectRatio="none">
          <polyline
            fill="none"
            stroke={color}
            strokeWidth="2"
            points={points}
          />
          {data.map((item, index) => {
            const x = (index / (data.length - 1 || 1)) * 100;
            const y = 100 - ((item[dataKey] - minValue) / range) * 80;
            return (
              <circle
                key={index}
                cx={x}
                cy={y}
                r="2"
                fill={color}
                className="hover:r-3 transition-all"
              />
            );
          })}
        </svg>
        <div className="flex justify-between mt-2">
          {data.map((item, index) => (
            <div key={index} className="text-center flex-1 min-w-0">
              <div className="text-xs text-gray-600 font-medium truncate">{item.month}</div>
              <div className="text-xs text-gray-500 truncate">{item[dataKey]}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Stats Cards Component
  const StatsCards = () => (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
      <StatsCard 
        title="Total Users"
        value={dashboardData.stats.totalUsers.toLocaleString()}
        icon="👥"
        color="green"
      />
      <StatsCard 
        title="Active Orders"
        value={dashboardData.stats.activeOrders.toLocaleString()}
        icon="📋"
        color="green"
      />
      <StatsCard 
        title="Total Packages"
        value={dashboardData.stats.totalPackages.toLocaleString()}
        icon="📦"
        color="green"
      />
      <StatsCard 
        title="Monthly Revenue"
        value={formatCurrencyShort(dashboardData.stats.monthlyRevenue)}
        icon="💰"
        color="green"
      />
    </div>
  );

  // Mobile Table Component
  const MobileTable = ({ title, data, type }) => (
    <div className="bg-white rounded-lg shadow">
      <div className="px-4 py-3 border-b border-gray-200">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900">{title}</h3>
      </div>
      <div className="divide-y divide-gray-200">
        {data.length > 0 ? (
          data.map((item, index) => (
            <div key={item.id || index} className="p-4 hover:bg-gray-50">
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {type === 'orders' ? `Order: ${item.id}` : `Invoice: ${item.id}`}
                  </p>
                  <p className="text-sm text-gray-600 truncate">{item.customer}</p>
                </div>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(item.status)} ml-2`}>
                  {type === 'billings' && item.status === 'paid' ? 'Lunas' :
                   type === 'billings' && item.status === 'pending' ? 'Pending' :
                   type === 'billings' && item.status === 'overdue' ? 'Terlambat' :
                   item.status?.replace(/_/g, ' ') || 'Unknown'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">
                  {type === 'orders' ? item.date : item.dueDate}
                </span>
                <span className="text-sm font-medium text-gray-900">
                  {formatCurrencyShort(item.amount)}
                </span>
              </div>
            </div>
          ))
        ) : (
          <div className="p-4 text-center text-gray-500 text-sm">
            No recent {type} found
          </div>
        )}
      </div>
    </div>
  );

  // Desktop Table Component
  const DesktopTable = ({ title, data, type }) => (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {type === 'orders' ? 'Order ID' : 'Invoice'}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.length > 0 ? (
              data.map((item, index) => (
                <tr key={item.id || index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {item.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.customer}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(item.status)}`}>
                      {type === 'billings' && item.status === 'paid' ? 'Lunas' :
                       type === 'billings' && item.status === 'pending' ? 'Pending' :
                       type === 'billings' && item.status === 'overdue' ? 'Terlambat' :
                       item.status?.replace(/_/g, ' ') || 'Unknown'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(item.amount)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                  No recent {type} found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Overview Content
  const OverviewContent = () => (
    <div className="space-y-4 sm:space-y-8">
      <StatsCards />
      
      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
        <div className="bg-white rounded-lg shadow p-3 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Monthly Revenue</h3>
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
              {currentTime.getFullYear()}
            </span>
          </div>
          {dashboardData.revenueChart.length > 0 ? (
            <SimpleBarChart 
              data={dashboardData.revenueChart}
              dataKey="revenue"
              color="#10B981"
            />
          ) : (
            <div className="h-48 sm:h-64 flex items-center justify-center text-gray-500 text-sm">
              No revenue data available
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-3 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Monthly Orders</h3>
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
              {currentTime.getFullYear()}
            </span>
          </div>
          {dashboardData.ordersChart.length > 0 ? (
            <SimpleLineChart 
              data={dashboardData.ordersChart}
              dataKey="orders"
              color="#10B981"
            />
          ) : (
            <div className="h-48 sm:h-64 flex items-center justify-center text-gray-500 text-sm">
              No orders data available
            </div>
          )}
        </div>
      </div>

      {/* Recent Tables */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-8">
        {/* Mobile Tables */}
        <div className="sm:hidden space-y-4">
          <MobileTable 
            title="Recent Orders"
            data={dashboardData.recentOrders}
            type="orders"
          />
          <MobileTable 
            title="Recent Billings"
            data={dashboardData.recentBillings}
            type="billings"
          />
        </div>

        {/* Desktop Tables */}
        <div className="hidden sm:block space-y-8">
          <DesktopTable 
            title="Recent Orders"
            data={dashboardData.recentOrders}
            type="orders"
          />
          <DesktopTable 
            title="Recent Billings"
            data={dashboardData.recentBillings}
            type="billings"
          />
        </div>
      </div>
    </div>
  );

  // Main render logic
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-64 px-4">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            <p className="mt-4 text-gray-600 text-center text-sm sm:text-base">Loading dashboard data...</p>
            <p className="text-xs text-gray-500 mt-2">{formatCurrentDateTime()}</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-center justify-center h-64 px-4">
          <div className="text-center max-w-md mx-auto">
            <div className="text-red-600 mb-4">
              <span className="text-3xl sm:text-4xl">⚠️</span>
            </div>
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">Failed to Load Dashboard</h3>
            <p className="text-gray-600 mb-4 text-sm sm:text-base">{error}</p>
            <p className="text-xs text-gray-500 mb-4">{formatCurrentDateTime()}</p>
            <button
              onClick={fetchDashboardData}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition text-sm sm:text-base"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }

    switch (activeTab) {
      case 'overview':
      default:
        return <OverviewContent />;
    }
  };

  // Sidebar Overlay for Mobile
  const SidebarOverlay = () => (
    <>
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </>
  );

  return (
    <RequireAdminRole user={contextUser} loading={authLoading}>
      <div className="flex h-screen bg-gray-50 overflow-hidden">
        <SidebarOverlay />
        
        {/* Sidebar - Mobile responsive */}
        <div className={`
          fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          <SidebarHeader 
            activeTab={activeTab} 
            setActiveTab={setActiveTab} 
            currentUser={currentUser}
            onClose={() => setSidebarOpen(false)}
          />
        </div>
        
        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <MobileHeader />
          <DesktopHeader />
          
          <main className="flex-1 overflow-auto p-3 sm:p-6">
            {renderContent()}
          </main>
        </div>
      </div>
    </RequireAdminRole>
  );
};

export default AdminDashboard;